import { type FC } from 'react';
import { useIntl } from 'react-intl';

import { useLocation } from '@xing-com/crate-core-router';
import { IconArrowLeft } from '@xing-com/icons';

import { MANAGE_USERS_PERMISSION } from '../../config/constants';
import { useTeampermissionsUserResources } from '../../hooks/use-teampermissions-user-resources';
import { EXTERNAL_LINKS } from '../../routes';
import { trackingParams, useNwtTrackingCompanies } from '../../utils/tracking';
import BaseButton from '../base-button';

import * as Styled from './logged-in-page.styles';

type LoggedInHeaderProps = {
  isBrandManagerEditor: boolean;
  showBackToCompanies: boolean;
};
export const LoggedInHeader: FC<LoggedInHeaderProps> = ({
  isBrandManagerEditor,
  showBackToCompanies,
}) => {
  const { formatMessage } = useIntl();
  const { pathname } = useLocation();

  const trackNwtEvent = useNwtTrackingCompanies();

  const { data: teampermissionsData, loading: tpLoading } =
    useTeampermissionsUserResources({
      permissions: MANAGE_USERS_PERMISSION,
    });

  if (tpLoading) return null;

  const teampermissionsLength =
    teampermissionsData?.viewer?.teamPermissions?.userResources?.collection
      ?.length;

  const showTeampermissionsButton =
    teampermissionsLength && teampermissionsLength > 0;

  return (
    <>
      <Styled.ButtonsContainer
        data-testid="logged-in-header"
        className={showBackToCompanies ? 'backToCompanies' : undefined}
      >
        {showBackToCompanies ? (
          <BaseButton to={'/companies'} icon={IconArrowLeft} size="small">
            {formatMessage({ id: 'SE_LABEL_COMPANIES' })}
          </BaseButton>
        ) : null}
        <Styled.ButtonsContainer>
          {showTeampermissionsButton ? (
            <Styled.BaseButton
              size="small"
              variant="secondary"
              href={EXTERNAL_LINKS.teampermissions()}
              trackingAttributes={trackingParams.teampermissions}
              onClick={() => {
                trackNwtEvent({
                  eventSchema: 'basic',
                  event: 'opened',
                  element: 'manage_team',
                  page: pathname,
                });
              }}
            >
              {formatMessage({ id: 'CP_TEAMPERMISSIONS' })}
            </Styled.BaseButton>
          ) : null}
          {isBrandManagerEditor ? (
            <Styled.BaseButton
              size="small"
              variant="secondary"
              href={EXTERNAL_LINKS.brandmanager()}
              trackingAttributes={trackingParams.brandmanager}
              onClick={() => {
                trackNwtEvent({
                  eventSchema: 'basic',
                  event: 'opened',
                  element: 'brand_manager',
                  page: pathname,
                });
              }}
            >
              {formatMessage({ id: 'CP_BRANDMANAGER' })}
            </Styled.BaseButton>
          ) : null}
          <Styled.BaseButton
            className={'createProfileButton'}
            size="small"
            variant="primary"
            href={EXTERNAL_LINKS.newCompany}
            trackingAttributes={trackingParams.createProfile}
            onClick={() => {
              trackNwtEvent({
                eventSchema: 'basic',
                event: 'opened',
                element: 'create_company',
                page: pathname,
              });
            }}
          >
            {formatMessage({ id: 'CP_CREATE_FREE_PROFILE' })}
          </Styled.BaseButton>
        </Styled.ButtonsContainer>
      </Styled.ButtonsContainer>
    </>
  );
};
